import { createStreamingMp3Encoder } from "./audioEncoding";
import { uint8ArrayToBase64 } from "./mp3EncoderHelpers";

// A WebM/Matroska container header (EBML + Segment + Info + Tracks, with no audio
// clusters) is at most ~1-2 KB regardless of bitrate. The stop-boundary tail we
// want to forgive — the user stopped within ms of a 10s rotation — is exactly that:
// a header with no audio frames, which decodeAudioData rejects. This bound is the
// discriminator between "benign header-only fragment" and "a real final segment
// that genuinely failed to decode (lost audio)". It is deliberately CONSERVATIVE
// (biased toward NEVER masking a real loss): a final segment carrying even ~0.3s of
// 48 kbps audio is already larger than this, so a genuinely-corrupt content segment
// still counts as a loss and warns. The only downside of a too-low bound is an
// occasional spurious warning if a browser emits an unusually large header — safe,
// never a silent loss. (Loss is "far worse than" a false alarm here — clinical audio.)
const HEADER_ONLY_MAX_BYTES = 2048;

// Container-aware stop-boundary tail bound. MP4/AAC (Safari/iOS) init segments
// (ftyp + moov) routinely exceed the WebM header size, so a webm-tuned 2048 bound
// false-warns on a benign Safari stop tail. Bias LARGE for non-webm — a too-large
// bound only risks an occasional spurious partial-loss warning, never a silent loss
// (the clinical-audio failure direction we must never take). Spec §12.4.
export function containerHeaderMaxBytes(containerMime) {
  const m = (containerMime || "").toLowerCase();
  if (m.indexOf("mp4") !== -1 || m.indexOf("aac") !== -1) return 16384;
  if (m.indexOf("ogg") !== -1) return 4096;
  return HEADER_ONLY_MAX_BYTES;
}

// Approximate decoded byte size of a base64 data URL WITHOUT allocating a copy of
// the (potentially multi-MB) base64 payload — base64 encodes 3 bytes per 4 chars.
function approxDataUrlBytes(dataUrl) {
  if (typeof dataUrl !== "string") return 0;
  const comma = dataUrl.indexOf(",");
  const b64Len = comma >= 0 ? dataUrl.length - comma - 1 : dataUrl.length;
  return Math.floor((b64Len * 3) / 4);
}

// Warn the clinician about partial audio loss after assembly: we produced a
// transcript, but some real audio didn't make it in — a NON-trailing segment
// failed to decode (failedCount), or fewer segments are present on disk than the
// final segment said to expect (presentCount < expectedTotal, e.g. one never
// persisted due to a storage-quota error).
//
// We compare PRESENT-ON-DISK (presentCount) against expectedTotal, NOT decoded
// against expected: a stop-boundary tail fragment is present on disk and counted
// in the recorder's `total`, but it has no decodable audio frames. Keying the
// shortfall check on decodedCount would fire on that benign fragment every time a
// recording ends just after a 10s rotation. presentCount only drops below expected
// when a recorded segment genuinely never reached disk — a real loss.
//
// Zero decoded is NOT a warn here — the caller throws and re-queues instead.
export function shouldWarnPartialLoss({ decodedCount, failedCount, presentCount, expectedTotal }) {
  if (decodedCount === 0) return false;
  if (failedCount > 0) return true;
  if (
    typeof expectedTotal === "number" &&
    expectedTotal > 0 &&
    typeof presentCount === "number" &&
    presentCount < expectedTotal
  ) {
    return true;
  }
  return false;
}

async function defaultDecodeAudio(ctx, dataUrl) {
  const res = await fetch(dataUrl); // data: URLs are fetchable in the browser
  const arrayBuffer = await res.arrayBuffer();
  // decodeAudioData detaches its input; slice() gives it an own copy.
  return ctx.decodeAudioData(arrayBuffer.slice(0));
}

// Stream a recording's stored segments into one MP3 without ever holding more
// than a single decoded segment in memory. `orderedKeys` are the IndexedDB keys
// in index order; `fetchSegmentAudio(key)` returns that segment's base64 data
// URL. Each segment is decoded, fed to the streaming encoder (which applies
// backpressure so only one chunk is in flight), then released.
//
// `channels: 2` stitches a two-channel (telemed) recording as stereo MP3 so the
// backend can transcribe each speaker separately. The returned `channels` is 2
// only if EVERY decoded segment really had two channels; otherwise it is 1 and
// the caller must upload as mono (a duplicated mono segment would make both
// "speakers" say the same thing).
//
// Returns { mp3DataUrl, decodedCount, failedCount, channels }; mp3DataUrl is a base64 data
// URL (the format api.transcript/parseRecording upload directly — so the drain
// never re-decodes the assembled recording, keeping memory bounded END TO END).
// It is null when nothing decoded OR the encoder fails mid-stream — caller fails
// loud and leaves the recording queued. A single undecodable segment is skipped
// (counted in failedCount), not fatal. The encoder ({ addBuffer, finish, dispose })
// is always disposed via the finally, so the worker is never leaked.
//
// `createEncoder` and `decodeAudio` are injectable seams for tests; production
// uses the real streaming encoder + a single AudioContext for the whole run.
export async function stitchSegmentsStreaming({
  orderedKeys,
  fetchSegmentAudio,
  durationSecsEstimate,
  onProgress,
  createEncoder = createStreamingMp3Encoder,
  decodeAudio,
  // Real container of the segments (e.g. "audio/mp4" for Safari). Drives the
  // trailing-fragment tail bound so a non-webm stop tail isn't false-warned.
  containerMime = "audio/webm",
  channels = 1,
}) {
  let ctx = null;
  let decode = decodeAudio;
  if (!decode) {
    const Ctor = window.AudioContext || window.webkitAudioContext;
    ctx = new Ctor();
    decode = (dataUrl) => defaultDecodeAudio(ctx, dataUrl);
  }

  const stereo = channels === 2;
  const encoder = stereo
    ? createEncoder({ durationSecsEstimate, onProgress, channels: 2 })
    : createEncoder({ durationSecsEstimate, onProgress });
  let decodedCount = 0;
  let failedCount = 0;
  let stereoIntact = stereo;

  try {
    // orderedKeys is sorted by segment index, so the LAST key is the final segment.
    // In the restart-per-segment recorder, only the final segment can be an
    // arbitrarily short stop-boundary tail — a header-only WebM fragment with no
    // decodable audio frames (the user stopped just after a 10s rotation). Every
    // non-final segment ran a full rotation window and carries real audio.
    for (let i = 0; i < orderedKeys.length; i++) {
      const key = orderedKeys[i];
      const isTrailing = i === orderedKeys.length - 1;
      let dataUrl;
      try {
        dataUrl = await fetchSegmentAudio(key);
      } catch (e) {
        // A read failure is a genuine retrieval error (DB/transaction), NOT the
        // benign header-only artifact — always count it, even on the trailing key.
        console.error("stitchSegmentsStreaming: failed to read segment", e);
        failedCount++;
        continue;
      }
      if (!dataUrl) continue; // empty (e.g. the empty final segment) — not a loss

      let buffer;
      try {
        buffer = await decode(dataUrl);
      } catch (e) {
        // A decode failure on the TRAILING segment is benign ONLY when the segment
        // is small enough to be a pure container header (no audio frames) — the
        // stop-boundary tail. The real audio is already in the prior segments, so
        // skip without counting a loss. A LARGER trailing segment that won't decode
        // carried real audio and genuinely corrupted: that IS a ~10s loss, so it
        // falls through and is counted — we must never silently drop real audio.
        if (isTrailing && approxDataUrlBytes(dataUrl) <= containerHeaderMaxBytes(containerMime)) {
          console.warn("stitchSegmentsStreaming: skipping header-only trailing boundary fragment — not a loss", e);
          continue;
        }
        // A non-trailing (or substantial trailing) undecodable segment IS a real
        // loss — skip it (keep the rest) but count it so the clinician is warned.
        console.error("stitchSegmentsStreaming: skipping undecodable segment", e);
        failedCount++;
        continue;
      }
      // An encoder/worker failure here is NOT recoverable (the encoder is dead),
      // so let it propagate to the catch below and abort the whole assembly.
      if (stereo && !(buffer.numberOfChannels >= 2)) stereoIntact = false;
      await encoder.addBuffer(buffer);
      decodedCount++;
    }

    if (decodedCount === 0) {
      // Nothing decodable — report nothing; the finally disposes the worker.
      return { mp3DataUrl: null, decodedCount, failedCount, allDecodeFailed: orderedKeys.length > 0 && decodedCount === 0 };
    }

    const mp3Bytes = await encoder.finish();
    // Base64 the assembled MP3 into a data URL so the drain hands it straight to
    // parseRecording → api.transcript WITHOUT a second decode/encode pass
    // (parseBlob would re-decode the whole recording and spike memory ~1.4GB/hr).
    const mp3DataUrl = "data:audio/mpeg;base64," + uint8ArrayToBase64(mp3Bytes);
    return { mp3DataUrl, decodedCount, failedCount, channels: stereoIntact ? 2 : 1 };
  } catch (e) {
    // Encoder/worker failure mid-stream (addBuffer or finish): a half-fed encoder
    // can't produce a valid MP3. Fail loud — return no blob so the drain leaves
    // the recording queued and retries.
    console.error("stitchSegmentsStreaming: encoder failure, aborting assembly", e);
    return { mp3DataUrl: null, decodedCount, failedCount, allDecodeFailed: orderedKeys.length > 0 && decodedCount === 0 };
  } finally {
    encoder.dispose(); // idempotent; guarantees the streaming worker is never leaked
    if (ctx) ctx.close();
  }
}
